import React from 'react';
import { BookIcon } from './BookIcon';
import { OndeAtuaCarousel } from './OndeAtuaCarousel';
import { OndeAtuaCarouselMobile } from './OndeAtuaCarouselMobile';
import { OndeAtuaTemaCard } from './OndeAtuaTemaCard';
import { YoutubeDialog } from './YoutubeDialog';
import {
  temaHasContent,
  toOndeAtuaTemas,
  type OndeAtuaData,
  type YoutubeVideoRef,
} from './helpers';
import { TITULO_FAIXA } from './typography';

interface OndeAtuaSectionProps {
  data?: OndeAtuaData;
  isEditMode?: boolean;
  /** Presente só no editor: `(slot do tema, índice do card)`. */
  onEditItem?: (tema: number, index: number) => void;
}

export const ONDE_ATUA_TITLE = 'Onde o MPF atua?';

/* `border` já na base, e a aba ativa pinta a borda da mesma cor do fundo: só
   a inativa tem traço no design, mas sem a borda nas duas elas ficariam com
   2px de diferença de altura e a linha de pílulas desalinharia. */
const TAB_BASE =
  'cursor-pointer rounded-full border px-5 py-2 text-base font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2';
/* `text-on-primary`, e não `text-surface`: o fundo da pílula ativa é o
   teal-900 nos dois temas, então o texto por cima precisa ser claro sempre —
   `--surface` inverte no alto contraste e apagaria o rótulo. */
const TAB_ACTIVE =
  'border-teal-900 bg-teal-900 text-on-primary dark:border-[color:var(--mpf-dark-tab-green-border)] dark:bg-[color:var(--mpf-dark-tag-green-bg)] dark:text-[color:var(--mpf-dark-tag-green-text)]';
/* Cores de token, e não um `bg-teal-900/10`: o Tailwind não aplica modificador
   de opacidade a cor declarada como `var(--…)` pura — a classe simplesmente
   não é gerada, e a pílula acabaria com o cinza padrão que o navegador dá a
   `<button>` (o preflight do Tailwind está desligado aqui). */
const TAB_IDLE =
  'border-teal-100 bg-teal-50 text-teal-900 hover:bg-teal-100 dark:border-night-500 dark:bg-night-700 dark:text-night-100 dark:hover:bg-night-600 dark:hover:text-night-50';

/**
 * Faixa "Onde o MPF atua?" (bloco `mpfOndeAtua`).
 *
 * Três temas em abas; cada aba troca a faixa inteira — subtítulo, texto, botão
 * e carrossel. No site só aparecem os temas com conteúdo; no editor os três
 * ficam visíveis, para quem edita enxergar os slots que ainda estão vazios.
 */
export const OndeAtuaSection: React.FC<OndeAtuaSectionProps> = ({
  data,
  isEditMode,
  onEditItem,
}) => {
  /* Tema sem nada preenchido não vira aba — nem no editor. Antes o editor via
     as três, para poder clicar numa vazia e preenchê-la; mas isso mostrava uma
     pílula "Tema 3" que a página publicada não teria, e o caminho para
     preencher já é a barra lateral, que traz os três fieldsets sempre. Basta
     um rótulo, texto, subtítulo ou card para a aba aparecer (`temaHasContent`). */
  const temas = React.useMemo(
    () => toOndeAtuaTemas(data).filter(temaHasContent),
    [data],
  );

  const [activeSlot, setActiveSlot] = React.useState(temas[0]?.slot ?? 1);
  const [video, setVideo] = React.useState<YoutubeVideoRef | null>(null);
  const tabsRef = React.useRef<HTMLDivElement>(null);

  // A aba escolhida pode sumir enquanto se edita (tema esvaziado, por
  // exemplo); nesse caso a faixa volta para a primeira que sobrou.
  const active =
    temas.find((tema) => tema.slot === activeSlot) ?? temas[0] ?? null;

  if (!active) return null;

  /** Setas ←/→ andam entre as abas, como manda o padrão de `tablist`. */
  const handleTabKeyDown = (event: React.KeyboardEvent) => {
    const step =
      event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    if (!step) return;
    event.preventDefault();
    const current = temas.findIndex((tema) => tema.slot === active.slot);
    const next = temas[(current + step + temas.length) % temas.length];
    setActiveSlot(next.slot);
    tabsRef.current
      ?.querySelector<HTMLButtonElement>(`#onde-atua-aba-${next.slot}`)
      ?.focus();
  };

  return (
    /* A faixa inteira é um card: traço em volta, raio 16 e padding 16, com os
       16 entre cabeçalho e painel vindo do `gap` (e não de uma margem no
       painel, que brigaria com o `key` que o remonta a cada troca de aba).
       Os filhos ficam em `stretch`, o padrão do flex: o design marca
       `align-items: center`, mas ali o conteúdo já ocupa a largura toda e
       centralizar encolheria cabeçalho e painel até o tamanho do texto.
       `not-prose` porque a seção também é usada como bloco numa página comum,
       onde o `prose` do corpo do conteúdo vazaria para dentro — a explicação
       inteira está em `AcompanheRedesSection`. */
    <section
      aria-labelledby="capa-onde-atua-titulo"
      className="not-prose flex flex-col gap-4 rounded-2xl border border-gray-200 bg-surface-container-lowest p-4 dark:border-night-850 dark:bg-night-800"
    >
      {/* Abaixo de `lg`, o cabeçalho empilha em 2 linhas — título em cima,
          abas embaixo, à esquerda (mockup do tablet) — porque em ~1000px
          sobra largura para as abas caberem ao lado do título, e aí o wrap
          "de emergência" do flex não entraria sozinho: o corte
          precisa ser explícito. `lg`+ volta à linha única de antes (título à
          esquerda, abas à direita, `justify-between`) — decisão de manter o
          desktop como estava: lá a largura já separa bem os dois grupos, e
          alternar o cabeçalho outra vez só para `lg` seria mexer no que não
          estava quebrado. */}
      <div className="flex flex-col items-start gap-3 lg:flex-row lg:flex-wrap lg:items-center lg:justify-between lg:gap-4">
        <div className="flex items-center gap-2">
          <BookIcon className="h-6 w-6 shrink-0 text-teal-600 dark:text-white" />
          <h2
            id="capa-onde-atua-titulo"
            className={`${TITULO_FAIXA} text-blue-800 dark:text-white`}
          >
            {data?.title || ONDE_ATUA_TITLE}
          </h2>
        </div>

        {temas.length > 1 && (
          <div
            ref={tabsRef}
            role="tablist"
            aria-label="Temas de atuação"
            className="flex flex-wrap items-center gap-2"
          >
            {temas.map((tema) => {
              const selected = tema.slot === active.slot;
              return (
                <button
                  key={tema.slot}
                  type="button"
                  role="tab"
                  id={`onde-atua-aba-${tema.slot}`}
                  aria-selected={selected}
                  aria-controls={`onde-atua-painel-${tema.slot}`}
                  // Só a aba ativa fica na ordem de tabulação: o Tab entra e
                  // sai do grupo de abas, e as setas andam entre elas.
                  tabIndex={selected ? 0 : -1}
                  onClick={() => setActiveSlot(tema.slot)}
                  onKeyDown={handleTabKeyDown}
                  className={`${TAB_BASE} ${selected ? TAB_ACTIVE : TAB_IDLE}`}
                >
                  {tema.label || `Tema ${tema.slot}`}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Régua do design: 2px em `--teal-600`, na largura do card. Decorativa,
          não separa conteúdo semântico — o cabeçalho já é o rótulo da seção.
          Os 16px acima e abaixo saem do `gap-4` da própria `<section>`; repetir
          o espaço como padding aqui dobraria a folga para 32px. */}
      <div
        aria-hidden="true"
        className="h-0.5 w-full shrink-0 bg-teal-600 dark:!bg-night-750"
      />

      {/* O `key` remonta o painel a cada troca de aba. É o que faz a animação
          de entrada tocar de novo (uma classe fixa só animaria na primeira
          montagem) e, de quebra, reinicializa o Swiper com o novo conjunto de
          slides. A animação em si está em `_capa-onde-atua.scss`, que também
          a desliga sob `prefers-reduced-motion`. */}
      <div
        key={active.slot}
        role="tabpanel"
        id={`onde-atua-painel-${active.slot}`}
        aria-labelledby={`onde-atua-aba-${active.slot}`}
        // Sem `tabIndex`: o painel já tem conteúdo focável (botão do tema,
        // setas do carrossel, links dos cards), então o padrão de `tablist`
        // não pede que ele próprio entre na ordem de tabulação.
        className="mpf-onde-atua__painel"
      >
        {/* Duas formas de DOM, não duas visibilidades do mesmo: no `lg`+ o
            texto é a coluna da esquerda, ao lado do carrossel; abaixo de `lg`
            (celular E tablet) ele é o primeiro slide de um carrossel único.
            Como a estrutura muda, e não só o que aparece, cada ramo é o seu
            próprio nó — mesma escolha da faixa de destaques
            (`DestaquesMaisMobile`). Quem liga e desliga é só CSS, sem JS
            medindo a tela.

            O corte é `lg` (1024px), e não mais `md`: um card de 4/12
            (≈250px numa largura de tablet) apertava demais o parágrafo — 2–3
            palavras por linha — e o botão `fullWidth` (que é
            `whitespace-nowrap`, ver `_buttons.scss`) não cabia no texto e
            vazava por cima da coluna vizinha, lido como "cortado" nas duas
            pontas. Em vez de empilhar o card acima do carrossel (1ª correção
            deste bug), o pedido de design foi outro: no tablet o
            comportamento vira o do celular — texto como 1º slide da mesma
            esteira — só que com `slidesPerView` fracionado, ver
            `OndeAtuaCarouselMobile`, para mostrar mais de um card por vez
            com espiada (peek) do próximo na borda. */}
        <div className="hidden gap-6 lg:grid lg:grid-cols-12">
          <div className="min-w-0 lg:col-span-4">
            <OndeAtuaTemaCard tema={active} />
          </div>

          <div className="min-w-0 lg:col-span-8">
            <OndeAtuaCarousel
              cards={active.cards}
              isEditMode={isEditMode}
              onPlay={setVideo}
              onEditItem={
                onEditItem
                  ? (index) => onEditItem(active.slot, index)
                  : undefined
              }
            />
          </div>
        </div>

        <div className="lg:hidden">
          <OndeAtuaCarouselMobile
            tema={active}
            isEditMode={isEditMode}
            onPlay={setVideo}
            onEditItem={
              onEditItem ? (index) => onEditItem(active.slot, index) : undefined
            }
          />
        </div>
      </div>

      <YoutubeDialog video={video} onClose={() => setVideo(null)} />
    </section>
  );
};

export default OndeAtuaSection;
